import React, { useRef, useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import api, { getUploadUrl } from '../../../lib/api';
import { Building2, Plus, Upload, Image, FileText, X, Eye, ClipboardList, Link2, Shield, MapPin, Trash2, Calendar, History, ChevronDown, ChevronUp, LayoutGrid, Briefcase } from 'lucide-react';

const PROPERTY_TYPES = [
  { value: 'condominium', label: 'Condominium' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'landed', label: 'Landed House' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'industrial', label: 'Industrial' },
];

const TIER_LABELS = { primary: 'Primary', secondary: 'Secondary', tertiary: 'Tertiary' };
const NEXT_TIER = ['primary', 'secondary', 'tertiary'];

function PicAssignmentsManager({ assignments, onChange, picUsers, picSearch, setPicSearch, filteredPicUsers }) {
  const list = Array.isArray(assignments) ? assignments : [];
  const usedTiers = new Set(list.map(a => a.tier));
  const nextAvailableTier = NEXT_TIER.find(t => !usedTiers.has(t)) || 'tertiary';
  const splitTotal = list
    .filter(a => a.split_mode === 'percentage')
    .reduce((sum, a) => sum + (parseFloat(a.split_value) || 0), 0);

  const addPic = () => {
    if (list.length >= 3) return;
    const newList = [...list, {
      user_id: '',
      tier: nextAvailableTier,
      split_mode: 'percentage',
      split_value: list.length === 0 ? 100 : 0,
    }];
    onChange(newList);
  };

  const removePic = (idx) => {
    onChange(list.filter((_, i) => i !== idx));
  };

  const updatePic = (idx, key, val) => {
    onChange(list.map((a, i) => i === idx ? { ...a, [key]: val } : a));
  };

  const distributeEqual = () => {
    if (list.length === 0) return;
    const equal = +(100 / list.length).toFixed(2);
    onChange(list.map(a => ({ ...a, split_mode: 'percentage', split_value: equal })));
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50" data-testid="pic-assignments-manager">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-gray-700 font-medium">PIC Assignments (Multi-PIC with Split)</Label>
        <div className="flex gap-2">
          {list.length > 1 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={distributeEqual}
              data-testid="pic-distribute-equal"
            >
              Equal Split
            </Button>
          )}
          {list.length < 3 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={addPic}
              data-testid="pic-add-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add PIC
            </Button>
          )}
        </div>
      </div>
      <p className="text-xs text-gray-500 mb-3">Up to 3 PICs (Primary / Secondary / Tertiary). Split can be % of PIC commission pool, or fixed RM amount per case.</p>

      {list.length === 0 && (
        <button
          type="button"
          className="w-full text-sm text-indigo-600 hover:bg-indigo-50 border border-dashed border-indigo-300 rounded-md py-3"
          onClick={addPic}
          data-testid="pic-add-first"
        >
          + Assign first PIC to this project
        </button>
      )}

      {list.map((a, idx) => {
        const selectedUser = picUsers.find(u => u.id === a.user_id);
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end mb-2 bg-white p-2 rounded border border-gray-100" data-testid={`pic-assignment-row-${idx}`}>
            {/* Tier */}
            <div className="col-span-2">
              <Label className="text-xs text-gray-600">Tier</Label>
              <Select value={a.tier} onValueChange={(v) => updatePic(idx, 'tier', v)}>
                <SelectTrigger className="h-9 text-xs" data-testid={`pic-tier-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(TIER_LABELS).map(([v, label]) => (
                    <SelectItem key={v} value={v}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* PIC user */}
            <div className="col-span-5">
              <Label className="text-xs text-gray-600">PIC User</Label>
              <Select value={a.user_id || ''} onValueChange={(v) => updatePic(idx, 'user_id', v)}>
                <SelectTrigger className="h-9 text-xs" data-testid={`pic-user-${idx}`}>
                  <SelectValue placeholder="Select PIC..." />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <div className="p-2 sticky top-0 bg-white border-b">
                    <Input
                      autoFocus
                      value={picSearch}
                      onChange={(e) => setPicSearch(e.target.value)}
                      placeholder="Search..."
                      className="h-7 text-xs"
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {filteredPicUsers.length === 0 ? (
                    <div className="px-3 py-3 text-center text-xs text-gray-500">No matches</div>
                  ) : (
                    filteredPicUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-xs">{u.full_name}</span>
                          {u.user_code && <span className="font-mono text-[10px] text-indigo-600">{u.user_code}</span>}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {selectedUser && (
                <p className="text-[10px] text-gray-400 mt-0.5 truncate">{selectedUser.email}</p>
              )}
            </div>
            {/* Split mode */}
            <div className="col-span-2">
              <Label className="text-xs text-gray-600">Split</Label>
              <Select value={a.split_mode || 'percentage'} onValueChange={(v) => updatePic(idx, 'split_mode', v)}>
                <SelectTrigger className="h-9 text-xs" data-testid={`pic-split-mode-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">% Split</SelectItem>
                  <SelectItem value="fixed">Fixed RM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Value */}
            <div className="col-span-2">
              <Label className="text-xs text-gray-600">{a.split_mode === 'fixed' ? 'RM' : '%'}</Label>
              <Input
                type="number"
                step={a.split_mode === 'fixed' ? '100' : '0.1'}
                value={a.split_value ?? ''}
                onChange={(e) => updatePic(idx, 'split_value', e.target.value)}
                className="h-9 text-xs"
                data-testid={`pic-split-value-${idx}`}
              />
            </div>
            {/* Remove */}
            <div className="col-span-1 flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 text-red-500 hover:bg-red-50"
                onClick={() => removePic(idx)}
                data-testid={`pic-remove-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );
      })}

      {list.length > 1 && (
        <div className={`mt-2 text-xs px-2 py-1 rounded ${Math.abs(splitTotal - 100) < 0.01 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`} data-testid="pic-split-total">
          % Split total: <strong>{splitTotal.toFixed(2)}%</strong>
          {Math.abs(splitTotal - 100) >= 0.01 && (
            <span className="ml-2">⚠ % splits should sum to 100% (fixed-RM rows excluded). Click "Equal Split" to auto-balance.</span>
          )}
        </div>
      )}
    </div>
  );
}

function CommissionScheduleManager({ schedules, onChange, baseMode, basePct, baseFixed }) {
  const list = Array.isArray(schedules) ? schedules : [];
  const [previewDate, setPreviewDate] = useState(() => new Date().toISOString().slice(0, 10));

  // Client-side resolver — mirrors backend resolve_commission_for_date.
  const resolved = (() => {
    const bd = (previewDate || '').slice(0, 10);
    const base = {
      mode: baseMode || 'percentage',
      pct: basePct !== '' && basePct !== undefined && basePct !== null ? parseFloat(basePct) : null,
      fixed: baseFixed !== '' && baseFixed !== undefined && baseFixed !== null ? parseFloat(baseFixed) : null,
      source: 'base',
      schedule: null,
    };
    if (!bd) return base;
    const parsed = list
      .filter(s => s.effective_from)
      .map(s => ({ ef: String(s.effective_from).slice(0, 10), s }))
      .sort((a, b) => b.ef.localeCompare(a.ef));
    for (const { ef, s } of parsed) {
      if (ef <= bd) {
        return {
          mode: s.commission_mode || base.mode,
          pct: s.commission_mode === 'fixed' ? null : (parseFloat(s.commission_percentage) || base.pct),
          fixed: s.commission_mode === 'fixed' ? (parseFloat(s.commission_fixed_amount) || base.fixed) : null,
          source: 'schedule',
          schedule: { effective_from: ef, note: s.note },
        };
      }
    }
    return base;
  })();

  const addSchedule = () => {
    const nextMode = baseMode || 'percentage';
    onChange([
      ...list,
      {
        id: undefined,
        effective_from: '',
        commission_mode: nextMode,
        commission_percentage: nextMode === 'percentage' ? (basePct ?? '') : '',
        commission_fixed_amount: nextMode === 'fixed' ? (baseFixed ?? '') : '',
        note: '',
      },
    ]);
  };

  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));
  const updateAt = (idx, key, val) => onChange(list.map((s, i) => i === idx ? { ...s, [key]: val } : s));

  const sorted = [...list].sort((a, b) => (a.effective_from || '').localeCompare(b.effective_from || ''));

  return (
    <div className="border border-indigo-200 rounded-lg p-4 bg-indigo-50/40" data-testid="commission-schedules-manager">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-indigo-600" />
          <Label className="text-gray-800 font-medium">Time-Based Commission Schedules</Label>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100"
          onClick={addSchedule}
          data-testid="add-commission-schedule-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Add Schedule
        </Button>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Rates that automatically take effect on a future date based on <strong>booking creation date</strong>.
        The latest schedule with <em>effective_from ≤ booking date</em> wins. Bookings created today
        will always use the rate above; this schedule does <strong>not</strong> retroactively change existing claims.
      </p>

      {list.length === 0 ? (
        <div className="text-xs text-gray-500 italic px-2 py-3 text-center bg-white border border-dashed border-indigo-200 rounded">
          No schedules yet — current commission rate above is used for every booking.
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((s) => {
            const idx = list.indexOf(s);
            const mode = s.commission_mode || 'percentage';
            return (
              <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-white p-2 rounded border border-indigo-100" data-testid={`commission-schedule-row-${idx}`}>
                <div className="col-span-3">
                  <Label className="text-xs text-gray-600">Effective From</Label>
                  <Input
                    type="date"
                    value={s.effective_from || ''}
                    onChange={(e) => updateAt(idx, 'effective_from', e.target.value)}
                    className="h-9 text-xs"
                    data-testid={`schedule-effective-from-${idx}`}
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-gray-600">Mode</Label>
                  <Select value={mode} onValueChange={(v) => updateAt(idx, 'commission_mode', v)}>
                    <SelectTrigger className="h-9 text-xs" data-testid={`schedule-mode-${idx}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">%</SelectItem>
                      <SelectItem value="fixed">Fixed RM</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-gray-600">{mode === 'fixed' ? 'Fixed RM' : '%'}</Label>
                  <Input
                    type="number"
                    step={mode === 'fixed' ? '100' : '0.1'}
                    value={mode === 'fixed' ? (s.commission_fixed_amount ?? '') : (s.commission_percentage ?? '')}
                    onChange={(e) => updateAt(idx, mode === 'fixed' ? 'commission_fixed_amount' : 'commission_percentage', e.target.value)}
                    className="h-9 text-xs"
                    data-testid={`schedule-rate-${idx}`}
                  />
                </div>
                <div className="col-span-4">
                  <Label className="text-xs text-gray-600">Note (optional)</Label>
                  <Input
                    type="text"
                    value={s.note || ''}
                    onChange={(e) => updateAt(idx, 'note', e.target.value)}
                    placeholder="e.g., Q3 promo bump"
                    className="h-9 text-xs"
                    data-testid={`schedule-note-${idx}`}
                  />
                </div>
                <div className="col-span-1 flex justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 text-red-500 hover:bg-red-50"
                    onClick={() => removeAt(idx)}
                    data-testid={`schedule-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Effective Rate Preview — sanity-check what a booking made on date X will see */}
      <div className="mt-3 p-3 bg-white rounded border border-indigo-200" data-testid="effective-rate-preview">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <Label className="text-xs text-gray-700 font-medium">Effective Rate Preview</Label>
            <p className="text-[10px] text-gray-500">Test what rate would apply for a booking made on…</p>
          </div>
          <Input
            type="date"
            value={previewDate}
            onChange={(e) => setPreviewDate(e.target.value)}
            className="h-8 text-xs w-40"
            data-testid="effective-rate-preview-date"
          />
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="effective-rate-preview-result">
          {resolved.mode === 'fixed' ? (
            <span className="text-sm font-semibold text-indigo-700">
              RM {Number(resolved.fixed || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
            </span>
          ) : (
            <span className="text-sm font-semibold text-indigo-700">
              {Number(resolved.pct || 0).toFixed(2)}%
            </span>
          )}
          <Badge
            className={resolved.source === 'schedule'
              ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
              : 'bg-gray-100 text-gray-600 border-gray-200'
            }
            variant="outline"
          >
            {resolved.source === 'schedule' ? 'From schedule' : 'Base rate'}
          </Badge>
          {resolved.source === 'schedule' && resolved.schedule && (
            <span className="text-[11px] text-gray-600">
              effective {resolved.schedule.effective_from}
              {resolved.schedule.note ? ` — ${resolved.schedule.note}` : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function AuditLogPanel({ propertyId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && entries.length === 0 && !loading) {
      setLoading(true);
      try {
        const res = await api.get(`/properties/${propertyId}/audit-log?limit=100`);
        setEntries(res.data?.entries || []);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load audit log');
      } finally {
        setLoading(false);
      }
    }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleString('en-MY', { dateStyle: 'medium', timeStyle: 'short' }) : '-';
  const formatVal = (v) => {
    if (v === null || v === undefined) return '∅';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  const renderDiff = (diff) => {
    if (!diff || Object.keys(diff).length === 0) return null;
    return (
      <ul className="text-xs space-y-1 mt-2 pl-3">
        {Object.entries(diff).map(([field, change]) => {
          if (field === 'commission_schedules' && change && typeof change === 'object' && 'added' in change) {
            return (
              <li key={field} className="text-gray-700">
                <strong>Commission Schedules:</strong>
                {change.added?.length > 0 && (
                  <span className="ml-1 text-green-700">+{change.added.length} added</span>
                )}
                {change.removed?.length > 0 && (
                  <span className="ml-1 text-red-700">-{change.removed.length} removed</span>
                )}
                {change.modified?.length > 0 && (
                  <span className="ml-1 text-amber-700">~{change.modified.length} modified</span>
                )}
              </li>
            );
          }
          return (
            <li key={field} className="text-gray-700">
              <strong>{field}:</strong>{' '}
              <span className="text-red-600 line-through">{formatVal(change?.from)}</span>{' → '}
              <span className="text-green-700">{formatVal(change?.to)}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50/50" data-testid="property-audit-log-panel">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-100 transition-colors rounded-lg"
        data-testid="property-audit-log-toggle"
      >
        <span className="flex items-center gap-2 font-medium text-gray-800">
          <History className="w-4 h-4 text-gray-600" /> Audit Log
          {entries.length > 0 && <Badge variant="outline" className="text-xs">{entries.length}</Badge>}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && (
        <div className="p-3 border-t border-gray-200 max-h-72 overflow-y-auto" data-testid="property-audit-log-list">
          {loading && <p className="text-xs text-gray-500">Loading…</p>}
          {error && <p className="text-xs text-red-600">{error}</p>}
          {!loading && !error && entries.length === 0 && (
            <p className="text-xs text-gray-500 italic">No audit entries yet.</p>
          )}
          {!loading && !error && entries.map((e) => (
            <div key={e.id} className="mb-3 pb-3 border-b last:border-b-0 last:pb-0 border-gray-100" data-testid={`audit-entry-${e.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-indigo-100 text-indigo-700 text-[10px]">{e.action}</Badge>
                <span className="text-xs text-gray-700">{e.changed_by_name || e.changed_by_role || 'system'}</span>
                <span className="text-[10px] text-gray-400">{fmtDate(e.changed_at)}</span>
              </div>
              {renderDiff(e.diff)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PropertyFormDialog({
  open, onOpenChange, editing, formData, setFormData, amenitiesInput, setAmenitiesInput,
  pendingImages, setPendingImages, pendingBrochure, setPendingBrochure,
  pendingLayouts = [], setPendingLayouts = () => {},
  pendingSalesKits = [], setPendingSalesKits = () => {},
  formCities,
  developers, states, bookingTemplates, developerTemplates,
  uploading, onSubmit, onRemoveExistingImage, onRemoveExistingBrochure,
  onRemoveExistingLayout = () => {}, onRemoveExistingSalesKit = () => {},
}) {
  const imageInputRef = useRef(null);
  const brochureInputRef = useRef(null);
  const layoutInputRef = useRef(null);
  const salesKitInputRef = useRef(null);

  const [picUsers, setPicUsers] = useState([]);
  const [picSearch, setPicSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/commission-claims/helpers/pic-users')
      .then(res => { if (!cancelled) setPicUsers(Array.isArray(res.data) ? res.data : []); })
      .catch(err => console.error('Failed to load PIC users:', err));
    return () => { cancelled = true; };
  }, [open]);

  const filteredPicUsers = picSearch.trim()
    ? picUsers.filter(u => {
        const s = picSearch.toLowerCase();
        return (
          (u.full_name || '').toLowerCase().includes(s) ||
          (u.email || '').toLowerCase().includes(s) ||
          (u.user_code || '').toLowerCase().includes(s) ||
          (u.phone || '').toLowerCase().includes(s)
        );
      })
    : picUsers;

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setPendingImages(prev => [...prev, ...files]);
  };
  const handleBrochureSelect = (e) => {
    const file = e.target.files[0];
    if (file) setPendingBrochure(file);
  };
  const handleLayoutSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setPendingLayouts(prev => [...prev, ...files]);
  };
  const handleSalesKitSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) setPendingSalesKits(prev => [...prev, ...files]);
  };
  const removePendingImage = (index) => setPendingImages(prev => prev.filter((_, i) => i !== index));
  const removePendingLayout = (index) => setPendingLayouts(prev => prev.filter((_, i) => i !== index));
  const removePendingSalesKit = (index) => setPendingSalesKits(prev => prev.filter((_, i) => i !== index));
  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-property-btn"><Plus className="w-4 h-4 mr-2" /> Add Property</Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-gray-900 text-xl">{editing ? 'Edit Property' : 'Add New Property'}</DialogTitle></DialogHeader>
        <div className="space-y-6 mt-4">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-gray-700">Property Name *</Label>
                <Input value={formData.name} onChange={e => set('name', e.target.value)} className="mt-1" placeholder="e.g., Skyline Residences" data-testid="property-name" />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-700">Description</Label>
                <Textarea value={formData.description} onChange={e => set('description', e.target.value)} className="mt-1" placeholder="Property description..." rows={3} />
              </div>
              <div className="col-span-2">
                <Label className="text-gray-700">Developer *</Label>
                <Select value={formData.developer || '_none'} onValueChange={v => set('developer', v === '_none' ? '' : v)}>
                  <SelectTrigger className="mt-1" data-testid="developer-select"><SelectValue placeholder="Select developer" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none"><span className="text-gray-400">Select a developer</span></SelectItem>
                    {developers.sort((a, b) => a.name.localeCompare(b.name)).map(dev => (
                      <SelectItem key={dev.id} value={dev.name}><div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-blue-500" />{dev.name}</div></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400 mt-1">Manage developers in Categories page</p>
              </div>
              <div>
                <Label className="text-gray-700">State *</Label>
                <Select value={formData.state || '_none'} onValueChange={v => setFormData(prev => ({ ...prev, state: v === '_none' ? '' : v, city: '' }))}>
                  <SelectTrigger className="mt-1" data-testid="state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none"><span className="text-gray-400">Select state</span></SelectItem>
                    {states.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700">City / Area *</Label>
                <Select value={formData.city || '_none'} onValueChange={v => set('city', v === '_none' ? '' : v)} disabled={!formData.state}>
                  <SelectTrigger className="mt-1" data-testid="city-select"><SelectValue placeholder={formData.state ? "Select city/area" : "Select state first"} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none"><span className="text-gray-400">Select city/area</span></SelectItem>
                    {formCities.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-gray-700 flex items-center gap-1"><MapPin className="w-4 h-4" /> Full Address *</Label>
                <Textarea value={formData.address} onChange={e => set('address', e.target.value)} className="mt-1" placeholder="e.g., Lot 123, Jalan Taman Bestari" rows={2} data-testid="property-address" />
              </div>
              <div>
                <Label className="text-gray-700">Postcode</Label>
                <Input value={formData.postcode || ''} onChange={e => set('postcode', e.target.value)} className="mt-1" placeholder="e.g., 80100" data-testid="property-postcode" />
              </div>
              <div>
                <Label className="text-gray-700">Tenure Type</Label>
                <Select value={formData.tenure_type || '_none'} onValueChange={v => set('tenure_type', v === '_none' ? '' : v)}>
                  <SelectTrigger className="mt-1" data-testid="property-tenure"><SelectValue placeholder="Select tenure" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none"><span className="text-gray-400">Not specified</span></SelectItem>
                    <SelectItem value="Freehold">Freehold</SelectItem>
                    <SelectItem value="Leasehold">Leasehold</SelectItem>
                    <SelectItem value="Malay Reserve">Malay Reserve</SelectItem>
                    <SelectItem value="Bumi Lot">Bumi Lot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700">Property Type</Label>
                <Select value={formData.property_type} onValueChange={v => set('property_type', v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{PROPERTY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-gray-700">Total Units</Label><Input type="number" value={formData.total_units} onChange={e => set('total_units', e.target.value)} className="mt-1" placeholder="e.g., 200" /></div>
              <div><Label className="text-gray-700">Min Price (RM)</Label><Input type="number" value={formData.price_range_min} onChange={e => set('price_range_min', e.target.value)} className="mt-1" placeholder="e.g., 500000" /></div>
              <div><Label className="text-gray-700">Max Price (RM)</Label><Input type="number" value={formData.price_range_max} onChange={e => set('price_range_max', e.target.value)} className="mt-1" placeholder="e.g., 1500000" /></div>
            </div>
          </div>

          {/* Exclusivity Toggle */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2"><Shield className="w-5 h-5" /> Project Exclusivity</h3>
            <div className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.is_exclusive ? 'border-[#4E50FF] bg-[#4E50FF]/5' : 'border-gray-200 bg-gray-50'}`} onClick={() => set('is_exclusive', !formData.is_exclusive)} data-testid="exclusive-toggle">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-6 rounded-full relative transition-colors ${formData.is_exclusive ? 'bg-[#4E50FF]' : 'bg-gray-300'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.is_exclusive ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{formData.is_exclusive ? 'Exclusive Project' : 'Non-Exclusive Project'}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{formData.is_exclusive ? 'Unit availability is linked' : 'Shared project — agents manually enter unit number'}</p>
                  </div>
                </div>
                <Badge className={formData.is_exclusive ? 'bg-[#4E50FF] text-white' : 'bg-gray-200 text-gray-600'}>{formData.is_exclusive ? 'Exclusive' : 'Non-Exclusive'}</Badge>
              </div>
            </div>
          </div>

          {/* Template Links */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2"><Link2 className="w-5 h-5" /> Booking Form Template</h3>
            <div>
              <Label className="text-gray-700">Select Booking Form Template</Label>
              <Select value={formData.booking_template_id || 'none'} onValueChange={v => set('booking_template_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select a booking template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none"><span className="text-gray-500">Use Default Template</span></SelectItem>
                  {bookingTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-500" />{t.name}{t.pdf_template_file && <Badge variant="outline" className="text-xs ml-2">Has PDF</Badge>}</div></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-3">
              <Label className="text-gray-700">Developer PDF Template</Label>
              <Select value={formData.developer_template_id || 'none'} onValueChange={v => set('developer_template_id', v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="developer-template-select"><SelectValue placeholder="Select developer template" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none"><span className="text-gray-500">No Developer Template</span></SelectItem>
                  {developerTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}><div className="flex items-center gap-2"><FileText className="w-4 h-4 text-purple-500" />{t.name}{t.ai_detection_status === 'completed' && <Badge variant="outline" className="text-xs ml-2 bg-purple-50 text-purple-600">{t.field_count} fields</Badge>}</div></SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Images Upload */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2"><Image className="w-5 h-5" /> Property Images</h3>
            <input type="file" ref={imageInputRef} onChange={handleImageSelect} accept="image/*" multiple className="hidden" />
            <div onClick={() => imageInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" /><p className="text-gray-600">Click to upload images</p>
            </div>
            {formData.images?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">Existing Images ({formData.images.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <img src={getUploadUrl(img)} alt={`Property ${idx + 1}`} className="w-full h-20 object-cover rounded border" onError={e => { e.target.style.display = 'none'; }} />
                      {editing && <button type="button" onClick={() => onRemoveExistingImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {pendingImages.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">New Images ({pendingImages.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {pendingImages.map((file, idx) => (
                    <div key={idx} className="relative group">
                      <img src={URL.createObjectURL(file)} alt={`New ${idx + 1}`} className="w-full h-20 object-cover rounded border border-blue-300" />
                      <button type="button" onClick={() => removePendingImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                      <Badge className="absolute bottom-1 left-1 text-xs bg-blue-500">New</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Property Layouts (Floor Plans) — visible to customer + agent */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
              <LayoutGrid className="w-5 h-5" /> Property Layouts <span className="text-xs font-normal text-gray-500">(Floor plans — public)</span>
            </h3>
            <p className="text-xs text-gray-500">Upload unit/floor plans. Images and PDF supported. Visible to customers and agents on the property detail page.</p>
            <input type="file" ref={layoutInputRef} onChange={handleLayoutSelect} accept="image/*,.pdf" multiple className="hidden" data-testid="layout-file-input" />
            <div
              onClick={() => layoutInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors"
              data-testid="layout-upload-dropzone"
            >
              <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600">Click to upload floor plans</p>
            </div>
            {formData.layouts?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">Existing Layouts ({formData.layouts.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {formData.layouts.map((layout, idx) => {
                    const isPdf = typeof layout === 'string' && layout.toLowerCase().endsWith('.pdf');
                    return (
                      <div key={idx} className="relative group" data-testid={`layout-existing-${idx}`}>
                        {isPdf ? (
                          <a href={getUploadUrl(layout)} target="_blank" rel="noopener noreferrer" className="w-full h-20 rounded border bg-gray-50 flex flex-col items-center justify-center text-gray-600 hover:bg-gray-100">
                            <FileText className="w-6 h-6 mb-1 text-red-500" />
                            <span className="text-[10px]">PDF</span>
                          </a>
                        ) : (
                          <img src={getUploadUrl(layout)} alt={`Layout ${idx + 1}`} className="w-full h-20 object-cover rounded border" onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        {editing && (
                          <button
                            type="button"
                            onClick={() => onRemoveExistingLayout(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`remove-layout-${idx}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {pendingLayouts.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">New Layouts ({pendingLayouts.length})</p>
                <div className="grid grid-cols-4 gap-2">
                  {pendingLayouts.map((file, idx) => {
                    const isPdf = file.type === 'application/pdf';
                    return (
                      <div key={idx} className="relative group">
                        {isPdf ? (
                          <div className="w-full h-20 rounded border border-amber-300 bg-amber-50 flex flex-col items-center justify-center text-amber-700">
                            <FileText className="w-6 h-6 mb-1" />
                            <span className="text-[10px] truncate px-1">{file.name}</span>
                          </div>
                        ) : (
                          <img src={URL.createObjectURL(file)} alt={`New layout ${idx + 1}`} className="w-full h-20 object-cover rounded border border-amber-300" />
                        )}
                        <button type="button" onClick={() => removePendingLayout(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <X className="w-3 h-3" />
                        </button>
                        <Badge className="absolute bottom-1 left-1 text-xs bg-amber-500">New</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Brochure */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2"><FileText className="w-5 h-5" /> Property Brochure</h3>
            <input type="file" ref={brochureInputRef} onChange={handleBrochureSelect} accept=".pdf,image/*" className="hidden" />
            {formData.brochure_url && !pendingBrochure && (
              <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                <FileText className="w-8 h-8 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Brochure Uploaded</p>
                  <a href={getUploadUrl(formData.brochure_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline flex items-center gap-1"><Eye className="w-3 h-3" /> View</a>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => brochureInputRef.current?.click()}>Replace</Button>
                {editing && <Button type="button" variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={onRemoveExistingBrochure}><Trash2 className="w-4 h-4" /></Button>}
              </div>
            )}
            {pendingBrochure && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <FileText className="w-8 h-8 text-blue-600" />
                <div className="flex-1"><p className="font-medium text-blue-800">{pendingBrochure.name}</p><p className="text-sm text-blue-600">Ready to upload</p></div>
                <Button type="button" variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setPendingBrochure(null)}><X className="w-4 h-4" /></Button>
              </div>
            )}
            {!formData.brochure_url && !pendingBrochure && (
              <div onClick={() => brochureInputRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" /><p className="text-gray-600">Click to upload brochure</p>
              </div>
            )}
          </div>

          {/* Sales Kit — agent-only marketing material (hidden from customer) */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-purple-600" />
              Sales Kit
              <Badge className="ml-1 bg-purple-100 text-purple-700 text-[10px] border border-purple-200">Agent view only</Badge>
            </h3>
            <p className="text-xs text-gray-500">Marketing material for agents — flyers, presentations, fact sheets. Customers will <strong>not</strong> see these files. PDF, images, Word, Excel, PowerPoint all supported.</p>
            <input
              type="file"
              ref={salesKitInputRef}
              onChange={handleSalesKitSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,image/*"
              multiple
              className="hidden"
              data-testid="sales-kit-file-input"
            />
            <div
              onClick={() => salesKitInputRef.current?.click()}
              className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors"
              data-testid="sales-kit-upload-dropzone"
            >
              <Upload className="w-8 h-8 mx-auto text-purple-400 mb-2" />
              <p className="text-gray-600">Click to upload sales kit files</p>
            </div>

            {formData.sales_kits?.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">Existing Files ({formData.sales_kits.length})</p>
                <div className="space-y-1">
                  {formData.sales_kits.map((kit) => (
                    <div key={kit.id} className="flex items-center gap-3 p-2 bg-purple-50/50 border border-purple-100 rounded-lg" data-testid={`sales-kit-existing-${kit.id}`}>
                      <FileText className="w-5 h-5 text-purple-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{kit.filename}</p>
                        <p className="text-[10px] text-gray-500">
                          {kit.size ? `${(kit.size / 1024).toFixed(1)} KB` : ''}
                          {kit.uploaded_by_name ? ` · uploaded by ${kit.uploaded_by_name}` : ''}
                          {kit.uploaded_at ? ` · ${new Date(kit.uploaded_at).toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}
                        </p>
                      </div>
                      <a
                        href={getUploadUrl(kit.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:bg-purple-100 rounded p-1.5"
                        title="View / Download"
                        data-testid={`sales-kit-view-${kit.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </a>
                      {editing && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:bg-red-50"
                          onClick={() => onRemoveExistingSalesKit(kit.id)}
                          data-testid={`remove-sales-kit-${kit.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {pendingSalesKits.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 font-medium">New Files ({pendingSalesKits.length})</p>
                <div className="space-y-1">
                  {pendingSalesKits.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-purple-100/40 border border-purple-200 rounded-lg">
                      <FileText className="w-5 h-5 text-purple-600 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
                        <p className="text-[10px] text-gray-500">{(file.size / 1024).toFixed(1)} KB · pending upload</p>
                      </div>
                      <Badge className="bg-purple-500 text-white text-[10px]">New</Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-500 hover:bg-red-50"
                        onClick={() => removePendingSalesKit(idx)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Commission Configuration */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2">Commission Configuration</h3>

            {/* Commission Mode toggle */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Commission Mode</Label>
                <Select
                  value={formData.commission_mode || 'percentage'}
                  onValueChange={(v) => set('commission_mode', v)}
                >
                  <SelectTrigger className="mt-1" data-testid="property-commission-mode">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage" data-testid="commission-mode-percentage">By % of Net Price</SelectItem>
                    <SelectItem value="fixed" data-testid="commission-mode-fixed">Fixed Amount per Unit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(formData.commission_mode || 'percentage') === 'percentage' ? (
                <div>
                  <Label className="text-gray-700">Commission %</Label>
                  <Input type="number" step="0.1" value={formData.commission_percentage || ''} onChange={e => set('commission_percentage', e.target.value)} className="mt-1" placeholder="e.g., 3.0" data-testid="property-commission-pct" />
                </div>
              ) : (
                <div>
                  <Label className="text-gray-700">Fixed Amount per Unit (RM)</Label>
                  <Input type="number" step="100" value={formData.commission_fixed_amount || ''} onChange={e => set('commission_fixed_amount', e.target.value)} className="mt-1" placeholder="e.g., 5000" data-testid="property-commission-fixed" />
                </div>
              )}
              <div><Label className="text-gray-700">Total Booking Fee (RM)</Label><Input type="number" step="100" value={formData.total_booking_fee} onChange={e => set('total_booking_fee', e.target.value)} className="mt-1" placeholder="e.g., 3000" data-testid="property-booking-fee" /></div>
              <div><Label className="text-gray-700">Platform Fee %</Label><Input type="number" step="0.1" value={formData.platform_fee_percentage} onChange={e => set('platform_fee_percentage', e.target.value)} className="mt-1" /></div>
              <div><Label className="text-gray-700">Referral Fee %</Label><Input type="number" step="0.1" value={formData.referral_fee_percentage} onChange={e => set('referral_fee_percentage', e.target.value)} className="mt-1" placeholder="0" data-testid="property-referral-fee" /></div>
              <div><Label className="text-gray-700">PIC Commission % <span className="text-xs text-gray-400">(default 7%)</span></Label><Input type="number" step="0.1" value={formData.pic_commission_percentage ?? ''} onChange={e => set('pic_commission_percentage', e.target.value)} className="mt-1" placeholder="7" data-testid="property-pic-commission-pct" /></div>
              <div><Label className="text-gray-700">Customer Cashback %</Label><Input type="number" step="0.1" value={formData.customer_cashback_percentage} onChange={e => set('customer_cashback_percentage', e.target.value)} className="mt-1" /></div>
              <div><Label className="text-gray-700">Introducer Commission %</Label><Input type="number" step="0.1" value={formData.introducer_commission_percentage} onChange={e => set('introducer_commission_percentage', e.target.value)} className="mt-1" /></div>
            </div>

            {/* Multi-PIC Assignment Manager */}
            <PicAssignmentsManager
              assignments={formData.pic_assignments || []}
              onChange={(arr) => {
                set('pic_assignments', arr);
                // Keep legacy fields in sync for backward compat
                set('pic_user_ids', arr.map(a => a.user_id).filter(Boolean));
                set('pic_user_id', arr[0]?.user_id || '');
              }}
              picUsers={picUsers}
              picSearch={picSearch}
              setPicSearch={setPicSearch}
              filteredPicUsers={filteredPicUsers}
            />

            {/* Fast Commission Options */}
            <div>
              <Label className="text-gray-700">Fast Commission Options Available for this Project</Label>
              <p className="text-xs text-gray-500 mt-1 mb-2">Tick which fast-comm options agents can choose when claiming. Disabling here removes the option from the claim form.</p>
              <div className="flex gap-6 mt-2">
                {['0', '50', '100'].map(opt => {
                  const checked = (formData.fast_comm_options || { '0': true, '50': true, '100': true })[opt] !== false;
                  const labels = { '0': '0% — Wait for developer payout', '50': '50% Upfront — No charges', '100': '100% Upfront — 16.67% fee' };
                  return (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer text-sm" data-testid={`fast-comm-option-${opt}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => set('fast_comm_options', {
                          ...(formData.fast_comm_options || { '0': true, '50': true, '100': true }),
                          [opt]: e.target.checked,
                        })}
                        className="w-4 h-4 accent-blue-600"
                      />
                      <span className="text-gray-700">{labels[opt]}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Time-Based Commission Schedules */}
            <CommissionScheduleManager
              schedules={formData.commission_schedules || []}
              onChange={(arr) => set('commission_schedules', arr)}
              baseMode={formData.commission_mode || 'percentage'}
              basePct={formData.commission_percentage}
              baseFixed={formData.commission_fixed_amount}
            />

            {/* Audit log — only when editing an existing property */}
            {editing && editing.id && (
              <AuditLogPanel propertyId={editing.id} />
            )}
          </div>

          {/* Amenities */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800 border-b pb-2">Amenities</h3>
            <div><Label className="text-gray-700">Amenities (comma separated)</Label><Input value={amenitiesInput} onChange={e => setAmenitiesInput(e.target.value)} placeholder="Swimming Pool, Gym, Parking" className="mt-1" /></div>
          </div>

          <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" disabled={uploading} data-testid="save-property-btn">
            {uploading ? 'Saving...' : (editing ? 'Update Property' : 'Create Property')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
